/**
 * `usePrepareBulkSave` — the multi-add pre-save step (identification/bulk.ts)
 * bound to the add store, the live Bridge state and the registry (ENG-4940 /
 * ENG-4942). The source footer calls it when "Add N Sources" is clicked.
 */
import { useAuth0 } from '#~/auth/index.js';
import { depot } from '#~/depot/index.js';
import { getTemplate } from '#~/device/registry/commands/get-template.js';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useContext, useEffect, useRef } from 'react';
import { Context as StoreContext } from '../store/context.js';
import type { BulkSavePrepared } from './bulk.js';
import { prepareBulkSave } from './bulk.js';
import type { IdentifyAtHostHandle } from './identify-at-host.js';
import { identifyAtHostLive } from './identify-at-host.js';

// The footer imports the dialog from here too, so its suites mock one module.
export { BulkBasicControlsDialog } from './bulk-basic-controls-dialog.jsx';

/**
 * Returns `prepare()`. It reads the RAW store (a click lands in the same tick
 * as the selection write, so the render snapshot can be stale — see
 * wiring.ts handleCommit) and resolves `undefined` when the wizard unmounted
 * while the identify wait ran, so a closed wizard never saves. Unmount cancels
 * every pending identify wait.
 */
export const usePrepareBulkSave = (): (() => Promise<T_Maybe<BulkSavePrepared>>) => {
  const storeApi = useContext(StoreContext);
  const { getAccessTokenSilently } = useAuth0();
  const handles = useRef(new Set<IdentifyAtHostHandle>());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const pending = handles.current;

    return () => {
      mounted.current = false;
      for (const handle of pending) handle.cancel();
      pending.clear();
    };
  }, []);

  return useLatestFn(async (): Promise<T_Maybe<BulkSavePrepared>> => {
    const state = storeApi?.getState();
    // Thrown, not `undefined`: the footer then saves without this step rather
    // than reading it as "the wizard closed".
    if (!state) throw new Error('Multi-add pre-save: the add store is not mounted');

    const { source } = state;
    const prepared = await prepareBulkSave({
      selectionIds: source.multiSelection,
      discoveries: source.bulkDiscoveries ?? source.discoveries,
      templates: source.bulkTemplates,
      brandDetected: source.bulkBrandDetected,
      brands: depot.getState().registry?.brands,
      fallbackAgentId: state.agentId,
      identify: (agentId, host) => {
        const handle = identifyAtHostLive(agentId, host);
        handles.current.add(handle);

        return handle.promise.finally(() => handles.current.delete(handle));
      },
      fetchTemplate: async (modelId) => getTemplate(modelId, await getAccessTokenSilently())
    });

    return mounted.current ? prepared : undefined;
  });
};
